import { useState, useRef, useEffect } from "react";
import Axios from "axios";
import React from "react";
import CircularProgress from '@mui/material/CircularProgress';
import { DocumentCheckIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const contract = `
<p>Introduction</p>
<p>This User Agreement ("Agreement") is made between DOCUP ("Company") and the user ("User") who creates an account on our application ("Application"). By creating an account and using our Application, the User agrees to the terms and conditions set forth in this Agreement.</p>

<p>1. Collection and Use of Data</p>
<p>1.1 Collected Data</p>
<p>Personal Information: Includes name, email address, contact details, and any other information provided by the User when creating the account or using the Application.</p>
<p>Client Information: Includes documents and data submitted by the User's clients as part of the services provided through the Application.</p>
<p>Usage Data: Includes information about how the User interacts with the Application, including IP addresses, browser types, and access times.</p>

<p>1.2 Use of Data</p>
<p>We use your data to:</p>
<ul>
<li>Provide, operate, and maintain our Application.</li>
<li>Improve, personalize, and develop our Application.</li>
<li>Understand and analyze how the User uses our Application.</li>
<li>Develop new products, services, features, and functionalities.</li>
<li>Communicate with the User, including for customer service, updates, and other information related to the Application.</li>
<li>Process transactions and manage subscriptions.</li>
<li>Detect and prevent fraud and other illegal activities.</li>
</ul>

<p>2. Limitations on Data Use</p>
<p>2.1 Prohibited Uses</p>
<p>We will never use your data to:</p>
<ul>
<li>Engage in business activities related to the User's industry, including immigration services.</li>
<li>Contact the User's clients except for technical support requests made by the User.</li>
<li>Sell or rent personal data to third parties.</li>
<li>Share personal data with third parties other than those listed in section 4.1.</li>
<li>Use the data for purposes other than those specified in this Agreement without the User's explicit consent.</li>
</ul>

<p>3. Data Protection and Security</p>
<p>3.1 Data Security</p>
<p>We implement industry-standard measures, including encryption and access controls, to ensure a level of security appropriate to the risk of processing personal data.</p>

<p>3.2 Data Retention</p>
<p>We will retain personal data only as long as necessary to achieve the purposes for which it was collected. Users can delete their accounts at any time, which will result in complete deletion of their data from our servers.</p>

<p>3.3 Location of Data</p>
<p>All data is stored in North America with a host that complies with industry standards (e.g., FedRAMP, EU Data Directive, FISMA).</p>

<p>4. Sharing and Disclosure of Data</p>
<p>4.1 Third-Party Service Providers</p>
<p>We may share data with third-party service providers who perform services on our behalf, such as payment processing, data analysis, and email delivery. Providers must maintain confidentiality and use data only for its intended purpose.</p>

<p>4.2 Legal Requirements</p>
<p>We may disclose data if required by law or in response to legitimate requests from public authorities.</p>

<p>5. User Responsibilities</p>
<p>5.1 Account Security</p>
<p>The User is responsible for maintaining the confidentiality of their account credentials and for all activities under their account, including actions of collaborators granted access.</p>

<p>5.2 Compliance with Laws</p>
<p>The User is responsible for complying with all applicable laws regarding collection and processing of data.</p>

<p>6. Termination</p>
<p>6.1 Termination by the User</p>
<p>The User may terminate this Agreement at any time by closing their account. All data will be deleted upon account termination.</p>

<p>6.2 Termination by the Company</p>
<p>The Company may terminate this Agreement and the User's access at any time, with or without cause and notice.</p>

<p>7. Governing Law and Arbitration</p>
<p>7.1 Governing Law</p>
<p>This Agreement and any disputes shall be governed by the laws of the Province of Quebec, Canada.</p>

<p>7.2 Arbitration</p>
<p>Disputes will be resolved exclusively by binding arbitration administered by a recognized arbitration body (e.g., ADR Institute of Canada) in Montreal, Quebec, Canada.</p>

<p>7.3 Scope of Arbitration</p>
<ul>
<li>Single arbitrator with expertise in commercial and technology contracts.</li>
<li>Decision is final and binding; judgment may be entered in any competent court.</li>
<li>Arbitration shall be conducted in English.</li>
</ul>

<p>7.4 Limitations on Litigation</p>
<p>Parties waive the right to bring lawsuits regarding this Agreement in any court other than arbitration. Nothing prevents seeking injunctive or equitable relief to protect IP or confidential information.</p>

<p>8. Warranty and Limitation of Liability</p>
<p>8.1 Limitation of Liability</p>
<p>DocUp and its suppliers, officers, affiliates, representatives, contractors, and employees shall not be liable for any subject matter of this Agreement under any contract, negligence, strict liability, or other theory:</p>
<ul>
<li>Errors, interruptions, or loss/corruption of data, cost of substitute goods/services/technology, or loss of business;</li>
<li>Indirect, exemplary, incidental, special, or consequential damages;</li>
<li>Matters beyond DocUp's reasonable control;</li>
<li>Amounts exceeding fees paid by the Customer for the Services in the 12 months prior to the act giving rise to liability, whether or not DocUp was advised of the possibility of such damages.</li>
</ul>

<p>8.2 Warranty</p>
<p>DocUp uses reasonable efforts to maintain Services with minimal errors and interruptions. Services may be temporarily unavailable for maintenance. Services are provided "as is"; DocUp disclaims all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement.</p>

<p>9. Data Security, Third-Party Services, and Collaboration</p>
<p>9.1 Third-Party Services</p>
<ul>
<li>DocUp may use third-party services to provide, process, or enhance the Application.</li>
<li>DocUp is not responsible for loss, delay, error, or disruption caused by third-party services.</li>
<li>Users acknowledge reliance on third-party services is at their own risk.</li>
</ul>

<p>9.2 Cybersecurity and Data Responsibility</p>
<ul>
<li>Users are responsible for the nature, legality, and sensitivity of uploaded/shared documents, including personal IDs, medical records, financial documents, or other regulated/confidential information.</li>
<li>DocUp does not monitor or control the type, content, or legality of uploaded documents.</li>
<li>DocUp implements industry-standard measures such as encryption and access controls but cannot guarantee against unauthorized access, disclosure, or cyberattacks.</li>
<li>Users shall indemnify and hold harmless DocUp from claims, losses, damages, or liabilities arising from uploaded documents or their use.</li>
</ul>

<p>9.3 Collaboration and Shared Accounts</p>
<ul>
<li>Users are responsible for all actions under their account, including actions of collaborators granted access.</li>
<li>DocUp is not liable for actions performed by collaborators or team members sharing account access.</li>
</ul>

<p>10. Ownership of Content</p>
<ul>
<li>All uploaded documents, files, or information remain the property of the user or entity that owns the content.</li>
<li>DocUp does not claim ownership over user content.</li>
<li>By uploading content, the user grants DocUp a non-exclusive, worldwide, royalty-free license to store, process, and display the content solely to provide the Services.</li>
</ul>

<p>11. Changing Fees</p>
<ul>
<li>DocUp may offer certain Services for free with limits (e.g., storage, users, features).</li>
<li>DocUp may modify, introduce, or discontinue fees, plans, or subscriptions at any time.</li>
<li>Users will be notified at least 15 days in advance of pricing changes.</li>
<li>Continued use after a pricing change constitutes acceptance. Users may cancel or downgrade before new fees take effect.</li>
<li>Failure to pay may result in suspension or termination of paid services; users retain ownership of uploaded content.</li>
</ul>

`;

export default function Register() {
  const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;

  const [registerPassword, setRegisterPassword] = useState("");
  const [confirmPassword, setRegisterConfirmPassword] = useState("");
  const [registerName, setRegisterName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [preferredLanguage, setPreferredLanguage] = useState("english");
  const [validated, setValidated] = useState(false);
  const [userExist, setUserExist] = useState(false);
  const [emailExist, setEmailExist] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [openWaiting, setOpenWaiting] = useState(false);

  const descriptionElementRef = useRef(null);

  useEffect(() => {
    if (open && descriptionElementRef.current) {
      descriptionElementRef.current.focus();
    }
  }, [open]);

  const register = async (e) => {
    e.preventDefault();
    setUserExist(false);
    setEmailExist(false);

    const hasMinLength = registerPassword.length >= 6;
    const hasUpperOrDigit = /[A-Z]/.test(registerPassword) || /\d/.test(registerPassword);
    if (!hasMinLength || !hasUpperOrDigit) {
      setError("Password does not meet the requirements below.");
      return;
    }
    if (registerPassword !== confirmPassword) {
      setError("Passwords don't match. Please try again.");
      return;
    }
    if (!acceptTerms) {
      setError("You must accept the Terms of Use to continue.");
      return;
    }

    setError('');
    setOpenWaiting(true);

    try {
      const response = await Axios({
        method: "POST",
        data: {
          consultant_Password: registerPassword,
          consultant_name: registerName,
          companyName,
          email,
          preferredLanguage,
        },
        withCredentials: true,
        url: `${backEndUrl}/users`,
      });

      setOpenWaiting(false);

      if (response.status === 200) {
        setValidated(true);
        setUserExist(false);
        setEmail("");
        setRegisterPassword("");
        setRegisterConfirmPassword("");
        setRegisterName("");
        setCompanyName("");
        setPreferredLanguage("english");
      } else if (response.status === 204) {
        setUserExist(true);
        setValidated(false);
      } else if (response.status === 205) {
        setEmailExist(true);
        setValidated(false);
      }
    } catch (err) {
      setOpenWaiting(false);
      alert("An error occurred during registration. Please try again.");
    }
  };

  const pwHasMinLength = registerPassword.length >= 6;
  const pwHasUpperOrDigit = /[A-Z]/.test(registerPassword) || /\d/.test(registerPassword);
  const showPwHints = registerPassword.length > 0;

  const inputClass =
    "block w-full rounded-xl border-0 py-2.5 px-4 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-500 sm:text-sm transition bg-white";

  const labelClass = "block text-sm font-medium text-slate-700 mb-1.5";

  return (
    <div className="flex min-h-screen bg-white">

      {/* ── Left panel: form ── */}
      <div className="relative flex flex-1 flex-col justify-center px-6 py-12 sm:px-10 lg:flex-none lg:w-[520px] xl:w-[580px] overflow-y-auto">

        {/* Decorative blobs */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -left-32 w-80 h-80 rounded-full opacity-[0.07]"
          style={{ background: 'radial-gradient(circle, #0ea5e9, transparent 70%)' }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-24 -right-24 w-72 h-72 rounded-full opacity-[0.06]"
          style={{ background: 'radial-gradient(circle, #38bdf8, transparent 70%)' }}
        />

        <div className="mx-auto w-full max-w-md">

          {/* Logo */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-gradient-to-br from-sky-400 to-sky-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-sky-200">
              <DocumentCheckIcon className="h-5 w-5" />
            </div>
            <span className="text-2xl font-bold text-slate-800 tracking-tight">ImmiSpace</span>
          </div>

          {/* Heading */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 leading-tight">
              Create your account
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Start managing your clients and documents today.
            </p>
          </div>

          {/* Success state */}
          {validated && (
            <div className="mb-6 flex items-start gap-3 rounded-xl bg-emerald-50 px-4 py-4 ring-1 ring-emerald-100">
              <CheckCircleIcon className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-emerald-800">Account created successfully!</p>
                <p className="text-sm text-emerald-700 mt-0.5">
                  Please{' '}
                  <a href="/login" className="font-bold underline hover:text-emerald-900 transition">
                    return to the home screen
                  </a>{' '}
                  to sign in.
                </p>
              </div>
            </div>
          )}

          {/* Error banners */}
          {(error || userExist || emailExist) && (
            <div className="mb-6 flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 ring-1 ring-red-100">
              <svg className="h-4 w-4 text-red-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-700 font-medium">
                {error || (userExist && "Username already taken. Please choose another.") || (emailExist && "This email is already registered.")}
              </p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={register} className="space-y-4">

            {/* Email */}
            <div>
              <label htmlFor="email" className={labelClass}>Email address</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder="you@example.com"
                className={inputClass}
              />
            </div>

            {/* Password row */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="password" className={labelClass}>Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={registerPassword}
                  onChange={(e) => { setRegisterPassword(e.target.value); setError(''); }}
                  placeholder="••••••••"
                  className={inputClass}
                />
                {showPwHints && (
                  <ul className="mt-2 space-y-1">
                    <li className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${pwHasMinLength ? 'text-emerald-600' : 'text-red-500'}`}>
                      {pwHasMinLength
                        ? <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>
                        : <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" /></svg>
                      }
                      At least 6 characters
                    </li>
                    <li className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${pwHasUpperOrDigit ? 'text-emerald-600' : 'text-red-500'}`}>
                      {pwHasUpperOrDigit
                        ? <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>
                        : <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" /></svg>
                      }
                      One uppercase letter or digit
                    </li>
                  </ul>
                )}
              </div>
              <div>
                <label htmlFor="confirmPassword" className={labelClass}>Confirm password</label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => { setRegisterConfirmPassword(e.target.value); setError(''); }}
                  placeholder="••••••••"
                  className={inputClass}
                />
              </div>
            </div>

            {/* Full name */}
            <div>
              <label htmlFor="name" className={labelClass}>Full name</label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                required
                value={registerName}
                onChange={(e) => setRegisterName(e.target.value)}
                placeholder="Jane Smith"
                className={inputClass}
              />
            </div>

            {/* Company name */}
            <div>
              <label htmlFor="companyName" className={labelClass}>Company name</label>
              <input
                id="companyName"
                name="companyName"
                type="text"
                autoComplete="organization"
                required
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Acme Immigration Inc."
                className={inputClass}
              />
              <p className="mt-1.5 text-xs text-slate-400">Displayed to your clients on the client portal.</p>
            </div>

            {/* Preferred language */}
            <div>
              <label htmlFor="preferredLanguage" className={labelClass}>Preferred language</label>
              <select
                id="preferredLanguage"
                name="preferredLanguage"
                value={preferredLanguage}
                onChange={(e) => setPreferredLanguage(e.target.value)}
                className={inputClass}
              >
                <option value="english">English</option>
                <option value="french">French</option>
              </select>
            </div>

            {/* Terms checkbox */}
            <div className="flex items-start gap-3 pt-1">
              <div className="flex h-5 items-center">
                <input
                  id="acceptTerms"
                  name="acceptTerms"
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                />
              </div>
              <label htmlFor="acceptTerms" className="text-sm text-slate-600 cursor-pointer">
                I have read, understand, and agree to the{' '}
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  className="font-semibold text-sky-600 hover:text-sky-500 underline transition"
                >
                  Terms of Use
                </button>
                .
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={openWaiting}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-sky-500 to-sky-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-sky-200 hover:from-sky-400 hover:to-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 active:scale-[0.98] transition-all duration-150 disabled:opacity-60"
            >
              {openWaiting ? <CircularProgress size={18} color="inherit" /> : "Create account"}
            </button>
          </form>

          {/* Sign in link */}
          <p className="mt-8 text-center text-sm text-slate-500">
            Already have an account?{' '}
            <a href="/login" className="font-semibold text-sky-600 hover:text-sky-500 transition">
              Sign in
            </a>
          </p>

          <p className="mt-6 text-center text-xs text-slate-400">
            © {new Date().getFullYear()} ImmiSpace. All rights reserved.
          </p>
        </div>
      </div>

      {/* ── Right panel: photo ── */}
      <div className="relative hidden flex-1 lg:block">
        <img
          alt="Immigration consultant at work"
          src="https://images.pexels.com/photos/2529179/pexels-photo-2529179.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-sky-900/60 via-slate-900/40 to-slate-900/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />

        {/* Right-panel caption */}
        <div className="absolute bottom-12 left-10 right-10">
          <p className="text-white text-2xl font-bold leading-snug">
            Streamline your immigration practice.
          </p>
          <p className="mt-2 text-sky-200 text-sm">
            Manage clients, documents, and checklists — all in one place.
          </p>
        </div>
      </div>

      {/* ── Terms of Use Modal ── */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="relative flex flex-col w-full max-w-2xl max-h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">Terms of Use</h2>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Modal body */}
            <div
              ref={descriptionElementRef}
              tabIndex={-1}
              className="flex-1 overflow-y-auto px-6 py-5 text-sm text-slate-600 leading-relaxed prose prose-sm max-w-none focus:outline-none"
              dangerouslySetInnerHTML={{ __html: contract }}
            />

            {/* Modal footer */}
            <div className="flex justify-end px-6 py-4 border-t border-slate-100 bg-slate-50">
              <button
                onClick={() => setOpen(false)}
                className="rounded-xl bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800 transition active:scale-[0.98]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Loading overlay ── */}
      {openWaiting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
          <CircularProgress sx={{ color: 'white' }} />
        </div>
      )}
    </div>
  );
}
